import {
  Grid,
  Flex,
  GridItem,
  AccordionIcon,
  AccordionPanel,
  AccordionButton,
  SkeletonCircle,
  Skeleton,
} from '@chakra-ui/react';
import PTORequestExpandedContent from '@/components/PTORequestExpandedContent';
import PTORequestWarnings from '@/components/PTORequestWarnings';
import { BankOfHoursBalanceResponse } from '@/services/ptos';
import { trioUITheme } from '@/styles/theme';
import Button from '@/trio-ui/Button';
import UserInitials from '@/trio-ui/UserInitials';
import { PTORequest, PTOStatus } from '@/types/api';
import { StaffMember } from '@/types/api/staffMember';
import {
  doestHaveEnoughHours,
  formatPTORequestDate,
  getPTORequestIntervalText,
  getTotalPTORequestHours,
  ptoTypeLabel,
} from '@/utils/pto';
import * as Styles from './PTORequestItem.styles';

export type PTORequestItemLayout = 'wide' | 'slim';

export type PTORequestItemComponentProps = {
  ptoRequest: PTORequest;
  onOpenPTOAnswerDialog: (status: PTOStatus.APPROVED | PTOStatus.DENIED) => void;
  isLoadingItemDetails?: boolean;
  staffMember?: StaffMember;
  isLoadingStaffMember?: boolean;
  balance?: BankOfHoursBalanceResponse;
  onAddCommentClick?: () => void;
  layout?: PTORequestItemLayout;
  overlappingPtos?: PTORequest[];
  onOpenEditDialog: () => void;
};

const PTORequestItemComponent = function ({
  ptoRequest,
  onOpenPTOAnswerDialog,
  isLoadingItemDetails = false,
  staffMember,
  balance,
  isLoadingStaffMember = false,
  onAddCommentClick,
  overlappingPtos = [],
  onOpenEditDialog,
  layout = 'wide',
}: PTORequestItemComponentProps): JSX.Element {
  const totalOfHours = getTotalPTORequestHours(ptoRequest);
  const notEnoughHours =
    !balance || ptoRequest.status !== PTOStatus.PENDING
      ? false
      : doestHaveEnoughHours(ptoRequest.ptoType, totalOfHours, balance);

  return (
    <Styles.Item data-testid="pto-request-item">
      {({ isExpanded }) => (
        <>
          <AccordionButton
            as="div"
            border="0"
            className="pto-request-item-accordion-button"
            backgroundColor={trioUITheme.colors['neutral-10']}
            borderRadius="8px"
            paddingBlock="8px"
            paddingInline="16px"
            data-testid="pto-request-item-accordion-button"
          >
            <Grid
              gridTemplateColumns={layout === 'wide' ? '2.5fr 1.5fr 200px 130px' : '2.5fr 1.5fr'}
              width="100%"
            >
              <GridItem display="flex" padding="8px 16px">
                {isLoadingStaffMember ? (
                  <SkeletonCircle size="9" className="shrink-0" style={{ marginRight: 15 }} />
                ) : (
                  <UserInitials
                    pictureUrl={staffMember?.person.pictureUrl || undefined}
                    firstName={staffMember?.person.firstName || ''}
                    lastName={staffMember?.person.lastName}
                    size={36}
                    className="shrink-0"
                  />
                )}

                <Flex direction="column">
                  {isLoadingStaffMember ? (
                    <Skeleton
                      style={{
                        height: 14,
                        width: 90,
                        marginTop: 4,
                        marginBottom: 3,
                        marginRight: 16,
                      }}
                    />
                  ) : (
                    <Flex>
                      <Styles.Name>{`${staffMember?.person.firstName || ''} ${
                        staffMember?.person.lastName || ''
                      }`}</Styles.Name>
                      <Styles.Date>
                        {formatPTORequestDate(new Date(ptoRequest.requestDate))}
                      </Styles.Date>
                    </Flex>
                  )}

                  <Styles.Description>
                    I’m requesting {totalOfHours} hrs of{' '}
                    <strong>{ptoTypeLabel[ptoRequest.ptoType]}</strong> for{' '}
                    {getPTORequestIntervalText(ptoRequest)}.
                  </Styles.Description>
                </Flex>
              </GridItem>

              <GridItem marginBlock="auto" padding="8px 16px">
                <PTORequestWarnings
                  status={ptoRequest.status}
                  overlapping={overlappingPtos.length > 0}
                  notEnoughHours={notEnoughHours}
                />
              </GridItem>

              {layout === 'wide' && (
                <Styles.AnswerButtons>
                  {ptoRequest.status === PTOStatus.PENDING ? (
                    <>
                      <Button
                        variantStyle="outline"
                        variantColor="blue"
                        size="medium"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenPTOAnswerDialog(PTOStatus.APPROVED);
                        }}
                        data-testid="pto-request-item-approve-button"
                      >
                        Approve
                      </Button>
                      <Button
                        variantStyle="outline"
                        variantColor="red"
                        size="medium"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenPTOAnswerDialog(PTOStatus.DENIED);
                        }}
                        data-testid="pto-request-item-deny-button"
                      >
                        Deny
                      </Button>
                    </>
                  ) : (
                    <Button
                      variantStyle="outline"
                      variantColor="blue"
                      size="medium"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenEditDialog();
                      }}
                      data-testid="pto-request-item-edit-button"
                    >
                      Edit
                    </Button>
                  )}
                </Styles.AnswerButtons>
              )}

              <GridItem
                padding="8px 16px"
                marginBlock="auto"
                marginInlineStart="auto"
                gridColumnStart={layout === 'slim' ? 1 : undefined}
                gridColumnEnd={layout === 'slim' ? 3 : undefined}
                width={layout === 'slim' ? '100%' : undefined}
                borderTop={
                  layout === 'slim' ? `1px solid ${trioUITheme.colors['neutral-30']}` : undefined
                }
                justifyContent={layout === 'slim' ? 'center' : 'right'}
                display="flex"
                alignItems="center"
              >
                <Styles.ShowContentText $layout={layout}>
                  {isExpanded ? 'Hide details' : 'See details'}
                </Styles.ShowContentText>
                <AccordionIcon boxSize={3} />
              </GridItem>
            </Grid>
          </AccordionButton>
          <AccordionPanel style={{ padding: 0 }}>
            <PTORequestExpandedContent
              isLoading={isLoadingItemDetails}
              bankOfHoursBalanceList={balance}
              ptoRequest={ptoRequest}
              overlappingPtoRequests={overlappingPtos}
              onAddCommentClick={onAddCommentClick}
              staffMember={staffMember}
              layout={layout}
            />
          </AccordionPanel>
        </>
      )}
    </Styles.Item>
  );
};

export default PTORequestItemComponent;
